import { useEffect, useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, NavLink, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { ScrollText, Moon, Sun, ShieldCheck, LogOut, KeyRound, BookOpen, GalleryVerticalEnd } from "lucide-react";
import { useTheme } from "./hooks/useTheme";
import Workspace from "./pages/Workspace";
import History from "./pages/History";
import FullBook from "./pages/FullBook";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Admin from "./pages/Admin";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ProtectedRoute, AdminRoute } from "./components/ProtectedRoute";
import { ChangePasswordDialog } from "./components/ChangePasswordDialog";

function Header() {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const location = useLocation();
  const [pwOpen, setPwOpen] = useState(false);
  const tabs = [
    { to: "/", label: "Boek", icon: BookOpen, exact: true },
    { to: "/brieven", label: "Alle Brieven", icon: GalleryVerticalEnd },
  ];
  if (user?.role === "admin") {
    tabs.push({ to: "/werkblad", label: "Werkblad", icon: ScrollText });
    tabs.push({ to: "/beheer", label: "Beheer", icon: ShieldCheck });
  }

  return (
    <header className="sticky top-0 z-40 border-b border-[var(--border-subtle)] bg-[var(--paper)]/85 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-[1500px] items-center justify-between px-5 sm:px-8">
        <NavLink to="/" className="flex items-center gap-3" data-testid="app-logo">
          <img src="/logo.png" alt="Pamaverliefd logo" className="h-9 w-9 rounded-lg object-cover shadow-sm" />
          <div className="leading-none">
            <div className="font-serif text-xl font-bold text-[var(--ink)]">Pamaverliefd</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--ink-3)]">AI Transcriptie</div>
          </div>
        </NavLink>

        <nav className="flex items-center gap-1">
          {tabs.map((t) => {
            const active = t.exact ? location.pathname === t.to : location.pathname.startsWith(t.to);
            return (
              <NavLink
                key={t.to}
                to={t.to}
                data-testid={`nav-${t.label.toLowerCase()}`}
                className={`flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors ${
                  active ? "bg-[var(--brand)] text-white" : "text-[var(--ink-2)] hover:bg-[var(--surface-muted)]"
                }`}
              >
                <t.icon className="h-4 w-4" />
                <span className="hidden sm:inline">{t.label}</span>
              </NavLink>
            );
          })}
          <button
            onClick={toggle}
            data-testid="theme-toggle"
            className="ml-1 flex h-9 w-9 items-center justify-center rounded-lg border border-[var(--border-subtle)] text-[var(--ink-2)] transition-colors hover:bg-[var(--surface-muted)]"
            aria-label="Thema wisselen"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          {user && (
            <div className="ml-1 flex items-center gap-2 border-l border-[var(--border-subtle)] pl-2">
              <span className="hidden max-w-[160px] truncate text-sm text-[var(--ink-3)] md:inline" data-testid="header-user-email" title={user.email}>{user.email}</span>
              <button
                onClick={() => setPwOpen(true)}
                data-testid="change-password-button"
                title="Wachtwoord wijzigen"
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-[var(--border-subtle)] text-[var(--ink-2)] transition-colors hover:bg-[var(--surface-muted)] hover:text-[var(--brand)]"
                aria-label="Wachtwoord wijzigen"
              >
                <KeyRound className="h-4 w-4" />
              </button>
              <button
                onClick={logout}
                data-testid="logout-button"
                title="Uitloggen"
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-[var(--border-subtle)] text-[var(--ink-2)] transition-colors hover:border-red-300 hover:text-red-600"
                aria-label="Uitloggen"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          )}
        </nav>
      </div>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </header>
  );
}

function AppLayout({ children }) {
  return (
    <>
      <Header />
      {children}
    </>
  );
}

function App() {
  useEffect(() => {
    document.title = "Pamaverliefd";
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/"
              element={<ProtectedRoute><AppLayout><FullBook /></AppLayout></ProtectedRoute>}
            />
            <Route
              path="/brieven"
              element={<ProtectedRoute><AppLayout><History /></AppLayout></ProtectedRoute>}
            />
            <Route
              path="/boek"
              element={<ProtectedRoute><AppLayout><FullBook /></AppLayout></ProtectedRoute>}
            />
            <Route
              path="/werkblad"
              element={<AdminRoute><AppLayout><Workspace /></AppLayout></AdminRoute>}
            />
            <Route
              path="/beheer"
              element={<AdminRoute><AppLayout><Admin /></AppLayout></AdminRoute>}
            />
          </Routes>
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
